import { createFileRoute } from "@tanstack/react-router";

import { jobNotificationCopy } from "@/lib/job-notifications.copy";
import type { JobNotificationDelivery } from "@/lib/job-notifications.server";

function escapeHtml(value: string) {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}

function notificationEmail(notification: JobNotificationDelivery) {
  const copy = jobNotificationCopy(notification, notification.locale);
  const label = {
    de: "Begner-Konto öffnen",
    fr: "Ouvrir le compte Begner",
    it: "Apri l’account Begner",
    en: "Open Begner account",
  }[notification.locale];
  const url = new URL("/login", notification.site_url);
  const jobId = notification.payload.job_id;
  url.searchParams.set(
    "next",
    notification.audience === "owner"
      ? jobId
        ? `/konto#auftrag-${jobId}`
        : "/konto#own-jobs-title"
      : "/konto#job-interests",
  );
  const title = notification.payload.job_title;
  return {
    subject: `${copy.title}: ${title}`,
    html: `<!doctype html><html><body style="margin:0;background:#f4f6f8;font-family:Arial,sans-serif;color:#1e293b"><div style="max-width:600px;margin:24px auto;background:white;border:1px solid #e2e8f0;border-radius:16px;overflow:hidden"><div style="background:#119dd4;color:white;padding:22px 28px;font-size:22px;font-weight:bold">Begner</div><div style="padding:28px"><h1 style="font-size:22px">${escapeHtml(copy.title)}</h1><p style="font-weight:bold">${escapeHtml(title)}</p><p>${escapeHtml(copy.body)}</p><p style="margin-top:24px"><a href="${escapeHtml(url.toString())}" style="display:inline-block;background:#119dd4;color:white;text-decoration:none;padding:12px 18px;border-radius:10px;font-weight:bold">${escapeHtml(label)}</a></p></div></div></body></html>`,
    text: `${copy.title}\n\n${title}\n\n${copy.body}\n\n${label}: ${url.toString()}`,
  };
}

export const Route = createFileRoute("/api/job-notification-cron")({
  staticData: { sitemap: false },
  server: {
    handlers: {
      POST: async ({ request }) => {
        const authorization = request.headers.get("authorization") ?? "";
        const token = authorization.startsWith("Bearer ") ? authorization.slice(7).trim() : "";
        if (token.length < 32 || token.length > 200)
          return Response.json({ error: "Unauthorized" }, { status: 401 });
        const { claimJobNotifications, finishJobNotification } =
          await import("@/lib/job-notifications.server");
        let notifications: JobNotificationDelivery[];
        try {
          notifications = await claimJobNotifications(token);
        } catch (error) {
          if (error instanceof Response && error.status === 401) {
            return Response.json({ error: "Unauthorized" }, { status: 401 });
          }
          return Response.json({ error: "Notification service unavailable" }, { status: 503 });
        }
        const { sendDecisionEmail } = await import("@/lib/email/send.server");
        let sent = 0;
        let failed = 0;
        for (const notification of notifications) {
          try {
            const mail = notificationEmail(notification);
            await sendDecisionEmail({
              to: notification.recipient_email,
              ...mail,
              idempotencyKey: `job-notification/${notification.notification_id}`,
            });
          } catch {
            await finishJobNotification({
              token,
              notificationId: notification.notification_id,
              success: false,
              error: "E-Mail konnte nicht versendet oder bestätigt werden.",
            }).catch(() => undefined);
            failed += 1;
            continue;
          }
          // A successful provider send must not be actively recorded as failed
          // merely because its acknowledgement encounters a transient outage.
          try {
            const confirmed = await finishJobNotification({
              token,
              notificationId: notification.notification_id,
              success: true,
            });
            if (!confirmed) throw new Error("Versandbestätigung nicht gespeichert.");
            sent += 1;
          } catch {
            failed += 1;
          }
        }
        return Response.json(
          { processed: notifications.length, sent, failed },
          { headers: { "Cache-Control": "no-store" } },
        );
      },
    },
  },
});
